import {Component, DestroyRef, DoCheck, OnInit} from '@angular/core';
import {ControlContainer, FormsModule, NgForm} from '@angular/forms';
import {LookupService} from '../../common/service/lookup.service';
import {HttpClient} from '@angular/common/http';
import {AppConfigService} from '../../common/service/app-config.service';
import {PlanModelService} from '../../common/service/plan-model.service';
import {take} from 'rxjs';
import {NgClass} from '@angular/common';
import {AggregateQuestionResponse} from '../../common/models/aggregate-question.response';
import {equals, isDefined} from '../../common/service/data-util';
import {PlanLodgementService} from '../../common/service/plan-lodgement.service';
import {NlrHasPermissionsPipe} from '../../common/pipe/nlr-has-permissions.pipe';
import {NlrPermissionsDisabledPipe} from '../../common/pipe/nlr-permissions-disabled-pipe';
import {NgEventBus} from 'ng-event-bus';
import {NlrFormFieldHasErrorPipe} from '../../common/pipe/nlr-form-field-has-error.pipe';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {switchMap} from 'rxjs/operators';

@Component({
    selector: 'additional-interest-notification-questions',
    imports: [NgClass, FormsModule, NlrHasPermissionsPipe, NlrPermissionsDisabledPipe, NlrFormFieldHasErrorPipe],
    templateUrl: './additional-interest-notification-questions.component.html',
    styleUrl: './additional-interest-notification-questions.component.scss',
    viewProviders: [{provide: ControlContainer, useExisting: NgForm}]
})
export class AdditionalInterestNotificationQuestionsComponent implements OnInit, DoCheck {
    readonly title = 'Additional Interests and Notifications and Questions';
    readonly tenure = 'former';

    questionLookups: any[];
    filteredQuestionLookups: any[] = [];
    aggregateInterestQuestions: any = {};
    attachmentsLookup: [];
    responseViewModel: AggregateQuestionResponse[] = [];

    previousInterests: any[] = [];
    previousFormerTenures: any[] = [];

    initialised = false;

    values = [
        {value: true, displayValue: 'Yes', active: true},
        {value: false, displayValue: 'No', active: true}
    ];

    constructor(
        private lookupService: LookupService,
        private http: HttpClient,
        private appConfigService: AppConfigService,
        private planModelService: PlanModelService,
        private planLodgementService: PlanLodgementService,
        private eventBus: NgEventBus,
        private destroyRef: DestroyRef
    ) {}

    ngDoCheck(): void {
        if (!this.initialised) {
            return;
        }
        const model = this.planModelService.getModel();
        if (model.formerTenures && !equals(model.formerTenures, this.previousFormerTenures)) {
            this.validateFormerTenures(this.previousFormerTenures, model.formerTenures);
            this.previousFormerTenures = [...model.formerTenures];
            this.showAdditionalQuestions();
            return;
        }
        if (model.interests && !equals(model.interests, this.previousInterests)) {
            this.previousInterests = [...model.interests];
            this.showAdditionalQuestions();
            return;
        }
    }

    ngOnInit(): void {
        this.eventBus
            .on('initiateInterestsContent')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                if (this.initialised) {
                    this.showAdditionalQuestions();
                }
            });

        if (!this.initialised) {
            this.init();
        }
    }

    init() {
        this.initialised = true;
        this.eventBus.cast('initiateAttachmentsGroup');
        this.questionLookups = this.lookupService.getLookups('aggregateInterestQuestions').filter(f => f.active);

        this.questionLookups.forEach((aggregate: any) => {
            this.aggregateInterestQuestions[aggregate.id] = {
                visible: false,
                value: null,
                yesInterest: aggregate.yesInterestCharacteristic,
                yesAlert: aggregate.yesAlert,
                noInterest: aggregate.noInterestCharacteristic,
                noAlert: aggregate.noAlert,
                question: aggregate.questionFormat,
                interestCharacteristics: aggregate.whenAllInterestCharacteristics
            };
        });

        // defer loading attachments until the model is loaded
        this.eventBus
            .on('nlr:plan-model-updated:model')
            .pipe(
                take(1),
                switchMap(() => {
                    return this.http.post(
                        `${this.appConfigService.settings.apiGatewayUrl}/attachment/attachments`,
                        this.planModelService.getModelForSerialisation()
                    );
                })
            )
            .subscribe((value: any) => {
                this.attachmentsLookup = value;
                this.showAdditionalQuestions();
            });
    }

    showAdditionalQuestions() {
        console.debug(this.title + ' show additional questions');

        this.filteredQuestionLookups.splice(
            0,
            this.filteredQuestionLookups.length,
            ...this.questionLookups.filter((l: any) => {
                if (!this.planModelService.getModel().interests || !this.planModelService.getModel().formerTenures) {
                    return false;
                }

                const matchingInterests = this.planModelService
                    .getModel()
                    .interests.filter(i => i.interestCode.interestCharacteristics)
                    .some(i =>
                        i.interestCode.interestCharacteristics.some(ic => this.aggregateInterestQuestions[l.id].interestCharacteristics.includes(ic))
                    );

                const matchingTenures = this.planModelService
                    .getModel()
                    .formerTenures.filter(ft => ft.formerTenureNotifications)
                    .some(ft =>
                        ft.formerTenureNotifications.some(
                            ftn =>
                                ftn.actionLookup &&
                                this.aggregateInterestQuestions[l.id].interestCharacteristics.includes(ftn.actionLookup.interestCharacteristics)
                        )
                    );

                return matchingInterests && matchingTenures;
            })
        );

        this.setAggregateInterests();
    }

    checkValue(identifier, value) {
        console.debug(this.title + ' check radio value');
        if (this.aggregateInterestQuestions.hasOwnProperty(identifier)) {
            this.aggregateInterestQuestions[identifier].value = value;
            this.setAggregateInterestResponse(identifier, value);
            const subscription = this.planLodgementService.save({showAlertOnSave: false}).subscribe();
            this.eventBus.cast('nlr:busy', subscription);
        }
    }

    setAggregateInterestResponse(identifier, value) {
        const currResponses = this.planModelService.getModel().aggregateInterestQuestions;

        currResponses.forEach(resp => {
            if (resp.code && resp.code === identifier) {
                resp.response = value;
                this.responseViewModel[identifier] = {response: value} as AggregateQuestionResponse;
                this.aggregateInterestQuestions[resp.code].value = resp.response;
            }
        });
    }

    setAggregateInterests() {
        if (!this.planModelService.getModel().aggregateInterestQuestions) {
            return;
        }

        const oldResponses = this.planModelService.getModel().aggregateInterestQuestions;
        const itemsToKeep = [];
        const itemsToAdd = [];

        this.filteredQuestionLookups.forEach(agg => {
            let match = false;
            oldResponses.forEach(resp => {
                if (resp.code && agg.id === resp.code && !match) {
                    match = true;
                    itemsToKeep.push({
                        code: resp.code,
                        id: resp.id,
                        response: resp.response
                    });
                    this.responseViewModel[agg.id] = {...resp} as AggregateQuestionResponse;
                    this.aggregateInterestQuestions[agg.id].value = resp?.response;
                }
            });
            if (!match) {
                itemsToAdd.push({
                    code: agg.id,
                    response: agg.response
                });
            }
        });
        this.planModelService
            .getModel()
            .aggregateInterestQuestions.splice(0, this.planModelService.getModel().aggregateInterestQuestions.length, ...itemsToKeep, ...itemsToAdd);
    }

    showQuestion(identifier) {
        if (this.aggregateInterestQuestions.hasOwnProperty(identifier)) {
            return this.aggregateInterestQuestions[identifier].question.replace('${ParentTenureTerm}', this.getTenure());
        } else {
            return null;
        }
    }

    getTenure() {
        return this.isInterestOnly() ? 'affected tenure' : this.tenure + ' tenure';
    }

    isInterestOnly() {
        return (
            isDefined(this.planModelService.getModel().planPurpose) &&
            this.planModelService.getModel().planPurpose.parentTenureAssociationType === 'AFFECTED_TENURE'
        );
    }

    labelHidden(identifier) {
        if (this.aggregateInterestQuestions[identifier].value !== null) {
            return (
                this.aggregateInterestQuestions[identifier].value &&
                this.aggregateInterestQuestions[identifier].yesAlert !== undefined &&
                this.aggregateInterestQuestions[identifier].yesAlert !== null
            );
        } else {
            return false;
        }
    }

    showLabel(identifier) {
        if (this.aggregateInterestQuestions.hasOwnProperty(identifier)) {
            if (this.aggregateInterestQuestions[identifier].value) {
                if (
                    this.aggregateInterestQuestions[identifier].yesInterest !== undefined &&
                    this.aggregateInterestQuestions[identifier].yesInterest !== null &&
                    !this.aggregateInterestQuestions[identifier].interestCharacteristics.includes(
                        this.aggregateInterestQuestions[identifier].yesInterest
                    )
                ) {
                    this.aggregateInterestQuestions[identifier].interestCharacteristics.push(this.aggregateInterestQuestions[identifier].yesInterest);
                }
                if (
                    this.aggregateInterestQuestions[identifier].yesAlert &&
                    this.aggregateInterestQuestions[identifier].yesAlert !== undefined &&
                    this.aggregateInterestQuestions[identifier].yesAlert !== null
                ) {
                    return this.aggregateInterestQuestions[identifier].yesAlert;
                }
            } else if (this.aggregateInterestQuestions[identifier].value !== null) {
                if (
                    this.aggregateInterestQuestions[identifier].noAlert &&
                    this.aggregateInterestQuestions[identifier].noAlert !== undefined &&
                    this.aggregateInterestQuestions[identifier].noAlert !== null
                ) {
                    return this.aggregateInterestQuestions[identifier].noAlert;
                }
            }
        }
    }

    validateFormerTenures(oldFormerTenures, newFormerTenures) {
        oldFormerTenures
            .filter(o => o.formerTenureNotifications && !newFormerTenures.some(n => n.id === o.id && n.parentPlan === o.parentPlan))
            .forEach(ft =>
                ft.formerTenureNotifications
                    .filter(ftn => ftn.actionLookup && ftn.actionLookup.interestCharacteristics)
                    .forEach(ftn =>
                        this.questionLookups
                            .filter(ql => ql.whenAllInterestCharacteristics.includes(ftn.actionLookup.interestCharacteristics))
                            .forEach(ql =>
                                this.planModelService
                                    .getModel()
                                    .aggregateInterestQuestions.filter(agg => agg.code === ql.id)
                                    .forEach(t => (t.response = null))
                            )
                    )
            );
    }
}
